/**
 * Permissions: who holds which role, and which Entra group grants it.
 *
 * Roles are held in Entra now, so nothing about a person is editable here:
 * membership of a mapped group is what grants a role, and the only writable
 * thing on the page is the group to role mapping on the Groups view.
 *
 * Platform admin only, like the two calls it lives on (`GET /users?includeRoles`
 * and `GET /role-groups`).
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle, Check, Loader2, Pencil, Plus, RotateCcw, Save, Search, Trash2, Users as UsersIcon,
} from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { inputClass } from '../components/Field'
import InfoTooltip from '../components/InfoTooltip'
import LoadingSpinner from '../components/LoadingSpinner'
import Select from '../components/Select'
import SettingsTabs from '../components/SettingsTabs'
import {
  GROUP_ID, MAPPABLE_ROLES, heldRoles, roleGroupsProblem, scopesFor,
} from '../lib/roleGroups'
import { daysSince, formatDateTime, getInitials } from '../lib/utils'
import { APP_ROLES, ROLE_LABELS } from '../types'
import type { AppRole, DirectoryUser, RoleGroupMapping } from '../types'

type View = 'people' | 'roles' | 'groups'

/** Past this, a person's group roles were read long enough ago to be worth
 *  doubting: they are only re-read when they sign in. */
const STALE_DAYS = 90

const chipBase = 'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap'
const groupChip = `${chipBase} border border-[#005EB8]/30 text-[#005EB8]`
const appChip = `${chipBase} bg-slate-100 text-slate-700`
const scopeChip = `${chipBase} border border-slate-200 bg-white text-slate-600 font-medium`
const headerRow = 'border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500'
const primaryButton = 'bg-[#005EB8] text-white px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-[#004a94] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap'
const quietButton = 'inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100'

const displayName = (u: DirectoryUser) => u.name || u.email || '(no name)'

/** The scopes a person edits within, across every role they hold. */
function scopeSummary(user: DirectoryUser): string[] | 'all' {
  const roles = heldRoles(user)
  if (roles.length === 1 && roles[0] === 'User') return []
  const scopes = roles.map(role => scopesFor(user, role))
  if (scopes.some(scope => scope === 'all')) return 'all'
  return [...new Set((scopes as string[][]).flat())].sort()
}


function RoleChips({ user }: { user: DirectoryUser }) {
  const roles = heldRoles(user)
  if (roles.length === 1 && roles[0] === 'User') {
    return <span className="text-[11px] text-slate-400">{ROLE_LABELS.User}</span>
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {roles.map(role => (
        <span
          key={role}
          title="From an Entra group, so it changes with the membership"
          className={groupChip}
        >
          {ROLE_LABELS[role]}
        </span>
      ))}
    </div>
  )
}

function ScopeCell({ user }: { user: DirectoryUser }) {
  const scopes = scopeSummary(user)
  if (scopes === 'all') return <span className={scopeChip}>Everything</span>
  if (!scopes.length) return <span className="text-[11px] text-slate-400">Reads everything</span>
  return (
    <div className="flex flex-wrap gap-1.5">
      {scopes.map(scope => <span key={scope} className={scopeChip}>{scope}</span>)}
    </div>
  )
}

/** Whether an object ID is the break-glass admin group, which is named in
 *  infrastructure and so never appears in the mapping. */
function isBreakGlass(groupId: string, breakGlass: string): boolean {
  return !!breakGlass && groupId.trim().toLowerCase() === breakGlass.trim().toLowerCase()
}

const BREAK_GLASS_NAME = 'Break-glass admin group'

function PeopleView({ users, known }: { users: DirectoryUser[]; known: boolean }) {
  // Nothing in this app narrows a role by scope yet, so the Scope column would
  // say the same word on every row. It comes back on its own once a scoped
  // grant exists.
  const anyScoped = users.some(user => {
    const scopes = scopeSummary(user)
    return scopes !== 'all' && scopes.length > 0
  })
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')

  const query = search.trim().toLowerCase()
  const filtered = users.filter(u => {
    if (query && !`${u.name || ''} ${u.email || ''}`.toLowerCase().includes(query)) return false
    if (role && !heldRoles(u).includes(role as AppRole)) return false
    return true
  })
  const filtering = !!query || !!role

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
        <h2 className="font-bold text-slate-800 flex items-center gap-1.5">
          <UsersIcon className="w-4 h-4 text-slate-500" />
          People
          <InfoTooltip>
            <strong>Read only</strong>
            <p>A role is gained or lost by joining or leaving the group that grants it, mapped on the Groups view.</p>
            <p>The change lands at their next sign-in.</p>
          </InfoTooltip>
        </h2>
        <span className="text-xs font-semibold text-slate-400">
          {filtering
            ? `${filtered.length} of ${users.length} shown`
            : `${users.length} ${users.length === 1 ? 'person' : 'people'}`}
        </span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Everyone who has signed in, and the access their groups give them.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email"
            aria-label="Search people by name or email"
            className={`${inputClass} pl-8`}
          />
        </div>
        <div className="w-44">
          <Select
            value={role}
            onChange={setRole}
            options={APP_ROLES.map(r => [r, ROLE_LABELS[r]] as const)}
            placeholder="Any role"
          />
        </div>
        {filtering && (
          <button
            type="button"
            onClick={() => { setSearch(''); setRole('') }}
            className="inline-flex items-center gap-1.5 py-2 text-xs font-semibold text-slate-500 hover:text-slate-800"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Clear filters
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">Nobody matches these filters.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[660px]">
            <thead>
              <tr className={headerRow}>
                <th className="py-2 pr-3 text-left font-semibold">Person</th>
                <th className="py-2 px-3 text-left font-semibold">Access</th>
                {anyScoped && <th className="py-2 px-3 text-left font-semibold">Scope</th>}
                <th className="py-2 pl-3 text-left font-semibold">Last signed in</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const age = u.lastSeenAt ? daysSince(u.lastSeenAt) : null
                const stale = age !== null && age > STALE_DAYS
                return (
                  <tr key={u.oid} className="align-top border-b border-slate-100">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[11px] flex-shrink-0">
                          {getInitials(u.email || u.name || '??')}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-800 truncate">{displayName(u)}</div>
                          <div className="text-[11px] text-slate-400 truncate">{u.email || '-'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3"><RoleChips user={u} /></td>
                    {anyScoped && <td className="py-3 px-3"><ScopeCell user={u} /></td>}
                    <td className="py-3 pl-3 whitespace-nowrap">
                      {u.lastSeenAt ? (
                        <>
                          <span className={`inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle ${stale ? 'bg-slate-300' : 'bg-[#00843D]'}`} />
                          <span className={stale ? 'text-[#B8640B] font-medium' : 'text-slate-600'}>{formatDateTime(u.lastSeenAt)}</span>
                        </>
                      ) : <span className="text-slate-400">Never</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Without the mapping there are no group names to print, so say why
          the column is full of object IDs. */}
      {!known && (
        <p className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
          The group mapping could not be read, so a group shows as its object ID.
        </p>
      )}
    </div>
  )
}

/** How many holders a card lists before folding the rest into a count. The
 *  User card is most of the directory, so without this it runs off the page. */
const HOLDERS_SHOWN = 6

function RoleCard({ title, holders, empty }: { title: string; holders: DirectoryUser[]; empty: string }) {
  const [all, setAll] = useState(false)
  const shown = all ? holders : holders.slice(0, HOLDERS_SHOWN)
  const hidden = holders.length - shown.length

  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-baseline justify-between gap-2 mb-2.5">
        <h3 className="text-[13px] font-bold text-slate-800">{title}</h3>
        <span className="text-[11px] font-bold text-slate-400 tabular-nums">{holders.length}</span>
      </div>
      {holders.length === 0 ? (
        <p className="text-xs text-[#B8640B]">{empty}</p>
      ) : (
        <>
          <div className="space-y-1.5">
            {shown.map(u => (
              <div key={u.oid} className="text-[12.5px] font-medium text-slate-700">{displayName(u)}</div>
            ))}
          </div>
          {hidden > 0 && (
            <button type="button" onClick={() => setAll(true)} className="mt-1.5 text-[11px] font-semibold text-[#005EB8] hover:underline">
              {hidden} more
            </button>
          )}
        </>
      )}
    </div>
  )
}

function RolesView({ users }: { users: DirectoryUser[] }) {
  const noRole = users.filter(u => heldRoles(u).join() === 'User')

  // Scope rows are built from what people actually hold: no role in this app
  // is narrowed by scope yet, so the table stays off the page until one is.
  const scopeRows = useMemo(() => {
    const rows = new Map<string, { role: AppRole; scope: string; people: number; groups: number }>()
    for (const user of users) {
      for (const role of heldRoles(user)) {
        const scopes = scopesFor(user, role)
        if (scopes === 'all') continue
        for (const scope of scopes) {
          const key = `${role}|${scope}`
          const row = rows.get(key) ?? { role, scope, people: 0, groups: 0 }
          row.people += 1
          // How many distinct groups carry this scope, which is what an admin
          // would have to change to widen or narrow it.
          row.groups = new Set(
            users.flatMap(u => (u.groupGrants ?? [])
              .filter(grant => grant.role === role && grant.scopes.includes(scope))
              .map(grant => grant.groupId.toLowerCase())),
          ).size
          rows.set(key, row)
        }
      }
    }
    return [...rows.values()].sort((a, b) => a.role.localeCompare(b.role) || a.scope.localeCompare(b.scope))
  }, [users])

  return (
    <div className="card p-6">
      <h2 className="font-bold text-slate-800 mb-1">By role</h2>
      <p className="text-sm text-slate-500 mb-4">
        Who holds each role. User is everyone whose groups grant nothing.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {MAPPABLE_ROLES.map(role => (
          <RoleCard
            key={role}
            title={ROLE_LABELS[role]}
            holders={users.filter(u => heldRoles(u).includes(role))}
            empty="Nobody holds this role."
          />
        ))}
        {/* Everyone else. Not a role anyone is granted: it is what is left when
            a person's groups grant nothing, and it still reads the whole
            portfolio. */}
        <RoleCard title={ROLE_LABELS.User} holders={noRole} empty="Everyone holds a role." />
      </div>

      {scopeRows.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <h3 className="text-[13px] font-bold text-slate-800 mb-2">Scopes</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className={headerRow}>
                  <th className="py-2 pr-3 text-left font-semibold">Scope</th>
                  <th className="py-2 px-3 text-left font-semibold">Role</th>
                  <th className="py-2 px-3 text-left font-semibold">People</th>
                  <th className="py-2 pl-3 text-left font-semibold">Where from</th>
                </tr>
              </thead>
              <tbody>
                {scopeRows.map(row => (
                  <tr key={`${row.role}|${row.scope}`} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5 pr-3"><span className={scopeChip}>{row.scope}</span></td>
                    <td className="py-2.5 px-3 text-slate-600">{ROLE_LABELS[row.role]}</td>
                    <td className="py-2.5 px-3 text-slate-600 tabular-nums">{row.people}</td>
                    <td className="py-2.5 pl-3 text-xs text-slate-500">
                      {row.groups === 1 ? 'From 1 group' : `From ${row.groups} groups`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function GroupsView({ users, saved, known, denied, breakGlass, onSaved }: {
  users: DirectoryUser[]
  saved: RoleGroupMapping[]
  known: boolean
  denied: boolean
  breakGlass: string
  onSaved: (mappings: RoleGroupMapping[]) => void
}) {
  const api = useApi()
  const [draft, setDraft] = useState<RoleGroupMapping[]>(saved)
  const [editing, setEditing] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  useEffect(() => { setDraft(saved) }, [saved])

  const seenWith = (groupId: string) =>
    users.filter(u => (u.groupGrants ?? []).some(grant => grant.groupId.toLowerCase() === groupId.trim().toLowerCase())).length

  // Groups that came back on someone's sign-in but are in no mapping. The
  // break-glass admin group is the one that always lands here.
  const unmapped = useMemo(() => {
    const mapped = new Set(draft.map(mapping => mapping.groupId.trim().toLowerCase()))
    const found = new Map<string, Set<AppRole>>()
    for (const user of users) {
      for (const grant of user.groupGrants ?? []) {
        const id = grant.groupId.toLowerCase()
        if (mapped.has(id)) continue
        found.set(id, (found.get(id) ?? new Set()).add(grant.role))
      }
    }
    return [...found.entries()].map(([groupId, roles]) => ({ groupId, roles: [...roles] }))
  }, [users, draft])

  if (!known) {
    return (
      <div className="card p-6 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#B8640B] shrink-0 mt-0.5" />
        <div>
          <h2 className="font-bold text-slate-800 mb-1">The mapping could not be read</h2>
          <p className="text-sm text-slate-500">
            {denied
              ? 'Only a platform admin can read the mapping. People is unaffected.'
              : 'It did not load, so People shows group IDs rather than names.'}
          </p>
        </div>
      </div>
    )
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved)
  const problem = roleGroupsProblem(draft)

  const update = (index: number, change: Partial<RoleGroupMapping>) => {
    setJustSaved(false)
    setRefusal(null)
    setDraft(rows => rows.map((row, i) => (i === index ? { ...row, ...change } : row)))
  }

  const addRow = () => {
    setJustSaved(false)
    setRefusal(null)
    setDraft(rows => [...rows, { groupId: '', groupName: '', role: MAPPABLE_ROLES[0], scopes: [] }])
    setEditing(draft.length)
  }

  const removeRow = (index: number) => {
    setJustSaved(false)
    setRefusal(null)
    setEditing(null)
    setDraft(rows => rows.filter((_, i) => i !== index))
  }

  const discard = () => {
    setDraft(saved)
    setEditing(null)
    setRefusal(null)
  }

  const save = async () => {
    setSaving(true)
    setRefusal(null)
    try {
      // The server canonicalises the list (trimmed scopes, sorted), so take
      // what it returns rather than the draft.
      const result = await api.setRoleGroups(draft)
      onSaved(result.mappings)
      setDraft(result.mappings)
      setEditing(null)
      setJustSaved(true)
    } catch (e) {
      setRefusal(e instanceof Error ? e.message : 'The mapping could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
        <h2 className="font-bold text-slate-800">Groups</h2>
        <button type="button" onClick={addRow} className={primaryButton}>
          <Plus className="w-3.5 h-3.5" /> Map a group
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        A group stands in for a role, so joining the team grants access.
      </p>

      {draft.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">
          No group is mapped yet, so no role is granted through Entra.
        </p>
      ) : (
        <div className="overflow-x-auto">
          {/* Kept inside the card's own width so a row being edited still shows
              its Done and Remove buttons rather than pushing them out of view. */}
          <table className="w-full text-sm min-w-[880px]">
            <thead>
              <tr className={headerRow}>
                <th className="py-2 pr-3 text-left font-semibold">Group ID</th>
                <th className="py-2 px-3 text-left font-semibold">Group name</th>
                <th className="py-2 px-3 text-left font-semibold">Grants</th>
                <th className="py-2 px-3 text-left font-semibold">Scope</th>
                <th className="py-2 px-3 text-left font-semibold">Seen</th>
                <th className="py-2 pl-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {draft.map((mapping, index) => {
                const open = editing === index
                const realId = GROUP_ID.test(mapping.groupId.trim())
                const seen = seenWith(mapping.groupId)
                const badId = !!mapping.groupId.trim() && !realId
                return (
                  <tr key={index} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="py-2.5 pr-3">
                      {open ? (
                        <input
                          type="text"
                          value={mapping.groupId}
                          onChange={e => update(index, { groupId: e.target.value })}
                          aria-label="Entra group object ID"
                          placeholder="00000000-0000-0000-0000-000000000000"
                          className={`${inputClass} font-mono text-xs ${badId ? 'border-[#B8640B]' : ''}`}
                        />
                      ) : (
                        <span className="font-mono text-[11px] text-slate-600 whitespace-nowrap">{mapping.groupId}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      {open ? (
                        <input
                          type="text"
                          value={mapping.groupName}
                          onChange={e => update(index, { groupName: e.target.value })}
                          aria-label="Group name"
                          placeholder="AAD-GAA-WW-TrackIT-..."
                          className={`${inputClass} text-xs`}
                        />
                      ) : (
                        <span className="text-slate-700 break-words">{mapping.groupName || <span className="text-slate-400">No name</span>}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      {open ? (
                        <div className="w-44">
                          <Select
                            value={mapping.role}
                            onChange={value => update(index, { role: value as AppRole })}
                            options={MAPPABLE_ROLES.map(role => [role, ROLE_LABELS[role]] as const)}
                          />
                        </div>
                      ) : <span className={appChip}>{ROLE_LABELS[mapping.role]}</span>}
                    </td>
                    {/* Scopes are stored but nothing in this app narrows a role
                        by one yet, so there is nothing to pick from. An edit
                        keeps whatever a row already carries. */}
                    <td className="py-2.5 px-3">
                      {mapping.scopes.length
                        ? <div className="flex flex-wrap gap-1.5">{mapping.scopes.map(scope => <span key={scope} className={scopeChip}>{scope}</span>)}</div>
                        : <span className={scopeChip}>Everything</span>}
                    </td>
                    <td className="py-2.5 px-3 whitespace-nowrap tabular-nums">
                      {/* A row still being typed has nothing to count yet, so it
                          is not held against the group. */}
                      {!realId
                        ? <span className="text-slate-400">-</span>
                        : seen === 0
                          ? <span className="text-[#B8640B] font-medium">Nobody yet</span>
                          : <span className="text-slate-600">{seen} {seen === 1 ? 'person' : 'people'}</span>}
                    </td>
                    <td className="py-2.5 pl-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setEditing(open ? null : index)}
                          aria-expanded={open}
                          aria-label={open ? `Finish editing ${mapping.groupName || 'this group'}` : `Edit ${mapping.groupName || 'this group'}`}
                          className={quietButton}
                        >
                          {open ? <><Check className="w-3 h-3" /> Done</> : <><Pencil className="w-3 h-3" /> Edit</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          aria-label={`Remove the mapping for ${mapping.groupName || mapping.groupId || 'this group'}`}
                          className="p-1 rounded-lg text-slate-400 hover:text-[#B8242E] hover:bg-slate-100"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
        {justSaved && !dirty && <span role="status" className="text-xs font-semibold text-[#00843D]">Saved. It applies the next time each person signs in.</span>}
        {dirty && problem && <span role="status" className="text-xs text-[#B8640B]">{problem}</span>}
        {refusal && <span role="status" className="text-xs text-[#B8242E]">{refusal}</span>}
        {dirty && (
          <button type="button" onClick={discard} className="text-xs font-semibold text-slate-500 hover:text-slate-800">
            Discard changes
          </button>
        )}
        <button type="button" onClick={save} disabled={!dirty || !!problem || saving} className={primaryButton}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>

      {unmapped.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <h3 className="text-[13px] font-bold text-slate-800 mb-2">Also granting roles</h3>
          <div className="space-y-1.5">
            {unmapped.map(group => (
              <div key={group.groupId} className="flex flex-wrap items-center gap-2 text-xs">
                {isBreakGlass(group.groupId, breakGlass)
                  ? <span className="text-slate-600 font-medium" title="Set in infrastructure">{BREAK_GLASS_NAME}</span>
                  : <span className="font-mono text-[11px] text-slate-600 break-all">{group.groupId}</span>}
                {group.roles.map(role => <span key={role} className={appChip}>{ROLE_LABELS[role]}</span>)}
                <span className="text-slate-400">
                  {seenWith(group.groupId)} {seenWith(group.groupId) === 1 ? 'person' : 'people'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}

export default function SettingsPermissions() {
  useDocumentTitle('Permissions')
  const api = useApi()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const isAdmin = !!user?.isAdmin
  const [view, setView] = useState<View>('people')
  // Held here rather than in the Groups view: a saved mapping renames the
  // groups the People view shows, and changes what the By role cards say.
  const [mappings, setMappings] = useState<RoleGroupMapping[] | null>(null)

  // Same gate as the other admin settings pages: bounce once auth resolves, so
  // the page never flashes during the initial MSAL settle.
  useEffect(() => {
    if (!authLoading && !isAdmin) navigate('/settings', { replace: true })
  }, [authLoading, isAdmin, navigate])

  // The mapping is a separate permission from the directory, so a refusal on
  // it only costs the group names, not the page.
  const { data, loading } = useData(async () => {
    const [users, groups] = await Promise.all([
      api.getUsers({ includeRoles: true }) as Promise<DirectoryUser[]>,
      api.getRoleGroups()
        .then(map => ({ map, denied: false }))
        .catch((e: unknown) => ({ map: null, denied: (e as { status?: number })?.status === 403 })),
    ])
    return { users, ...groups }
  })

  const loadedMappings = data?.map?.mappings
  useEffect(() => { if (loadedMappings) setMappings(loadedMappings) }, [loadedMappings])

  if (authLoading || !isAdmin || loading) return <LoadingSpinner />

  const users = [...(data?.users ?? [])].sort((a, b) => displayName(a).localeCompare(displayName(b)))
  const known = !!data?.map
  const list = mappings ?? []
  const denied = !!data?.denied
  const breakGlass = data?.map?.breakGlassGroupId ?? ''

  const tabs: [View, string, number][] = [
    ['people', 'People', users.length],
    ['roles', 'By role', MAPPABLE_ROLES.length + 1],
    ['groups', 'Groups', list.length],
  ]

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500">Configure integrations and defaults</p>
      </div>

      <SettingsTabs active="permissions" />

      <div className="inline-flex gap-1 p-1 mb-5 bg-slate-100 rounded-xl">
        {tabs.map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            aria-pressed={view === id}
            onClick={() => setView(id)}
            className={`px-3.5 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              view === id ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
            {/* No count on Groups when the mapping could not be read: zero
                would read as "nothing is mapped", which is a different fact. */}
            {!(id === 'groups' && !known) && (
              <span className="ml-1.5 text-[11px] font-bold text-slate-400 tabular-nums">{count}</span>
            )}
          </button>
        ))}
      </div>

      {view === 'people' && <PeopleView users={users} known={known} />}
      {view === 'roles' && <RolesView users={users} />}
      {view === 'groups' && <GroupsView users={users} saved={list} known={known} denied={denied} breakGlass={breakGlass} onSaved={setMappings} />}
    </div>
  )
}
